import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import "./error.css";

const BASE = process.env.PUBLIC_URL || "";

/* ---------- inline SVG icons (no emoji) ---------- */
const sp = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true };
const Cap = () => (<svg {...sp}><path d="M21.42 10.92a1 1 0 0 0-.02-1.84L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.83l8.57 3.91a2 2 0 0 0 1.66 0z" /><path d="M22 10v6" /><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5" /></svg>);
const Gear = () => (<svg {...sp}><circle cx="12" cy="12" r="3" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></svg>);
const Book = () => (<svg {...sp}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>);
const Compass = () => (<svg {...sp}><circle cx="12" cy="12" r="10" /><path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" /></svg>);
const Star = () => (<svg {...sp}><path d="m12 2 2.4 7.4H22l-6 4.6 2.3 7L12 16.8 5.7 21l2.3-7-6-4.6h7.6z" /></svg>);
const Pen = () => (<svg {...sp}><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" /></svg>);
const Wrench = () => (<svg {...sp}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94z" /></svg>);
const Ruler = () => (<svg {...sp}><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.4 2.4 0 0 1 0-3.4l2.6-2.6a2.4 2.4 0 0 1 3.4 0z" /><path d="M14.5 12.5 16 11M11.5 9.5 13 8M8.5 6.5 10 5M6.5 14.5 8 13M9.5 17.5 11 16" /></svg>);
const Bolt = () => (<svg {...sp}><path d="M13 2 3 14h9l-1 8 10-12h-9z" /></svg>);
const Arrow = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>);

const MODES = {
  notfound: {
    eyebrow: "Error 404",
    title: "Page Not Found",
    text: "The page you’re looking for has wandered off campus. Let’s walk you back to familiar ground.",
    glyph: <Cap />,
    floats: [<Book key="b" />, <Compass key="c" />, <Star key="s" />, <Pen key="p" />],
    spin: false,
  },
  construction: {
    eyebrow: "Coming Soon",
    title: "Page Under Construction",
    text: "We’re building something great here. Please bear with us — it’ll be ready for you very soon.",
    glyph: <Gear />,
    floats: [<Wrench key="w" />, <Ruler key="r" />, <Bolt key="z" />, <Gear key="g" />],
    spin: true,
  },
};

const QUICK = [
  { to: "/about-nagarjuna", label: "About NCET" },
  { to: "/departments", label: "Departments" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

// Dual-mode status page: 404 "Not Found" and "Under Construction".
// Navy academic theme; an animated graduation-cap / gears medallion with
// floating academic icons; subtle campus photo wash behind. Honors
// prefers-reduced-motion (loops + entrance fall back to a simple fade).
const ErrorContent = ({ mode = "notfound" }) => {
  const reduce = useReducedMotion();
  const m = MODES[mode] || MODES.notfound;
  const isBuild = mode === "construction";

  const fade = {
    hidden: { opacity: 0, y: 18 },
    show: (i = 0) => (reduce
      ? { opacity: 1, y: 0 }
      : { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.15 + i * 0.09, ease: [0.22, 1, 0.36, 1] } }),
  };
  const floatAnim = (d) => (reduce ? {} : { animate: { y: [0, -11, 0] }, transition: { duration: 3.4, repeat: Infinity, ease: "easeInOut", delay: d } });
  const digit = (dir) => (reduce
    ? {}
    : { initial: { opacity: 0, x: 34 * dir, scale: 0.8 }, animate: { opacity: 1, x: 0, scale: 1 }, transition: { type: "spring", stiffness: 150, damping: 15, delay: 0.1 } });

  return (
    <section className={`ep ${isBuild ? "ep--build" : "ep--404"}`} aria-labelledby="ep-title">
      <span className="ep__grid" aria-hidden="true" />
      <span className="ep__orb ep__orb--a" aria-hidden="true" />
      <span className="ep__orb ep__orb--b" aria-hidden="true" />
      <span className="ep__wash" aria-hidden="true" style={{ backgroundImage: `url("${BASE}/images/Why NCET/tech_summit.jpg")` }} />

      <div className="ep__inner container">
        {/* ---- animated art ---- */}
        <div className="ep__stage" aria-hidden="true">
          {!isBuild && <motion.span className="ep__digit" {...digit(-1)}>4</motion.span>}

          <div className="ep__medallion">
            <span className="ep__ring" />
            <span className="ep__ring ep__ring--2" />
            <motion.span
              className="ep__glyph"
              {...(reduce ? {} : {
                animate: m.spin ? { rotate: 360 } : { y: [0, -6, 0] },
                transition: m.spin
                  ? { duration: 16, repeat: Infinity, ease: "linear" }
                  : { duration: 3, repeat: Infinity, ease: "easeInOut" },
              })}
            >
              {m.glyph}
            </motion.span>
            {m.floats.map((ic, i) => (
              <motion.span className={`ep__chip ep__chip--${i}`} key={i} {...floatAnim(i * 0.45)}>{ic}</motion.span>
            ))}
          </div>

          {!isBuild && <motion.span className="ep__digit" {...digit(1)}>4</motion.span>}
        </div>

        {/* ---- copy ---- */}
        <motion.span className="ep__eyebrow" variants={fade} initial="hidden" animate="show" custom={0}>
          <span className="ep__eyebrow-dot" />{m.eyebrow}
        </motion.span>
        <motion.h1 id="ep-title" className="ep__title" variants={fade} initial="hidden" animate="show" custom={1}>{m.title}</motion.h1>
        <motion.p className="ep__text" variants={fade} initial="hidden" animate="show" custom={2}>{m.text}</motion.p>

        <motion.div className="ep__actions" variants={fade} initial="hidden" animate="show" custom={3}>
          <Link to="/" className="ep__btn ep__btn--primary">Back to Home <Arrow /></Link>
          <Link to="/contact" className="ep__btn ep__btn--ghost">Contact Us</Link>
        </motion.div>

        <motion.div className="ep__links" variants={fade} initial="hidden" animate="show" custom={4}>
          <span className="ep__links-label">Quick links</span>
          <span className="ep__links-row">
            {QUICK.map((q) => (
              <Link key={q.to} to={q.to} className="ep__pill">{q.label}</Link>
            ))}
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default ErrorContent;
